import React, { useMemo } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { getAlternative } from '../data/alternatives';
import { useSEO } from '../hooks/useSEO';
import { useJsonLd } from '../hooks/useJsonLd';
import {
  buildArticleSchema,
  buildBreadcrumbSchema,
  buildFaqPageSchema,
  buildWebPageSchema,
} from '../utils/seoStructuredData';
import FooterSection from '../components/landing/FooterSection';
import BlogContentRenderer from '../components/BlogContentRenderer';

const AlternativeComparisonPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const alt = getAlternative(slug ?? '');

  useSEO(
    alt
      ? {
          title: alt.seoTitle,
          description: alt.seoDescription,
          canonical: `/alternatives/${alt.slug}`,
          keywords: alt.tags,
          ogType: 'article',
          article: { tags: alt.tags, publishedTime: '2026-05-20' },
        }
      : {
          title: 'Comparison Not Found',
          description: 'This comparison page does not exist.',
          robots: 'noindex, nofollow',
        }
  );

  const jsonLd = useMemo(() => {
    if (!alt) return null;
    const path = `/alternatives/${alt.slug}`;
    return [
      buildWebPageSchema({
        path,
        name: alt.seoTitle,
        description: alt.seoDescription,
      }),
      buildArticleSchema({
        path,
        headline: `Retrosive vs ${alt.name}`,
        description: alt.seoDescription,
        keywords: alt.tags,
      }),
      buildBreadcrumbSchema([
        { name: 'Home', path: '/' },
        { name: 'Alternatives', path: '/alternatives' },
        { name: `vs ${alt.name}`, path },
      ]),
      buildFaqPageSchema(alt.faqs),
    ];
  }, [alt]);

  useJsonLd(jsonLd);

  if (!alt) {
    return <Navigate to="/alternatives" replace />;
  }

  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-slate-900 via-orange-800 to-slate-900 text-white pt-14 pb-10">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <nav aria-label="Breadcrumb" className="text-sm text-orange-200/80 mb-6">
            <ol className="flex flex-wrap gap-2">
              <li>
                <Link to="/" className="hover:text-white">
                  Home
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li>
                <Link to="/alternatives" className="hover:text-white">
                  Alternatives
                </Link>
              </li>
              <li aria-hidden>/</li>
              <li className="text-white font-medium">vs {alt.name}</li>
            </ol>
          </nav>
          <span className="text-4xl mb-4 block" aria-hidden>
            {alt.emoji}
          </span>
          <h1 className="text-3xl sm:text-4xl font-bold mb-4">
            Retrosive vs {alt.name}
          </h1>
          <p className="text-orange-100/90 text-lg leading-relaxed">{alt.hubSummary}</p>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-4 sm:px-6 py-10 pb-8">
        <BlogContentRenderer sections={alt.content} />
      </article>

      <section className="max-w-3xl mx-auto px-4 sm:px-6 pb-12" aria-labelledby="comparison-faq">
        <h2 id="comparison-faq" className="text-2xl font-bold text-slate-900 mb-6">
          {alt.name} vs Retrosive — FAQ
        </h2>
        <div className="space-y-4">
          {alt.faqs.map((faq) => (
            <article key={faq.question} className="border border-gray-200 rounded-xl p-5">
              <h3 className="font-semibold text-slate-900 mb-2">{faq.question}</h3>
              <p className="text-sm text-gray-700 leading-relaxed">{faq.answer}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="py-10 border-t border-gray-100 bg-slate-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <h2 className="text-lg font-bold text-slate-900 mb-4">More comparisons</h2>
          <div className="flex flex-wrap gap-2">
            {[
              { slug: 'retrosive-vs-easyretro', label: 'EasyRetro' },
              { slug: 'retrosive-vs-parabol', label: 'Parabol' },
              { slug: 'retrosive-vs-ludi', label: 'Ludi' },
              { slug: 'retrosive-vs-miro', label: 'Miro' },
              { slug: 'retrosive-vs-figjam', label: 'FigJam' },
              { slug: 'retrosive-vs-mural', label: 'Mural' },
              { slug: 'retrosive-vs-retrium', label: 'Retrium' },
              { slug: 'retrosive-vs-lucidspark', label: 'Lucidspark' },
              { slug: 'retrosive-vs-microsoft-whiteboard', label: 'MS Whiteboard' },
              { slug: 'retrosive-vs-teamretro', label: 'TeamRetro' },
            ]
              .filter((c) => c.slug !== alt.slug)
              .map((c) => (
                <Link
                  key={c.slug}
                  to={`/alternatives/${c.slug}`}
                  className="px-3 py-1.5 text-sm bg-white border border-gray-200 rounded-lg hover:border-orange-300 hover:text-orange-600 transition-colors"
                >
                  vs {c.label}
                </Link>
              ))}
          </div>
          <p className="mt-6 text-sm text-gray-500">
            <Link to="/alternatives" className="text-orange-600 hover:underline font-medium">
              ← View all retrospective tool comparisons
            </Link>
          </p>
        </div>
      </section>

      <FooterSection />
    </div>
  );
};

export default AlternativeComparisonPage;
